// Nastavení sales sekce (owner): zapnutí automatik, přednostní trenéři, ceny, převzetí z Notionu, testy.
import React, { useEffect, useState } from "react";
import { Settings, Send, Receipt, Mail, MessageSquare, Download, Power } from "lucide-react";
import { Modal, HlavickaOkna, Field, Zapinatko } from "../ui.jsx";
import { toast } from "../Toast.jsx";
import { confirmDialog } from "../confirmHost.jsx";
import { getCurrentUser } from "../../lib/auth.js";
import { salesGet, salesPost } from "./salesApi.js";
import { Cipy, Oznameni, SkupinaNadpis } from "./salesUi.jsx";

const TESTY = [
  { typ: "sms", ikona: Send, popis: "Testovací SMS na číslo sales" },
  { typ: "fapi", ikona: Receipt, popis: "Zálohová faktura na mě a hned storno" },
  { typ: "email", ikona: Mail, popis: "Ukázkový e‑mail s pozvánkou mně" },
  { typ: "slack", ikona: MessageSquare, popis: "Týdenní souhrn do Slacku" },
];

export default function NastaveniSales({ open, onClose }) {
  const owner = String(getCurrentUser()?.role || "").toLowerCase() === "owner";
  const [data, setData] = useState(null);
  const [n, setN] = useState(null);
  const [chyba, setChyba] = useState("");
  const [busy, setBusy] = useState("");
  const [vysledky, setVysledky] = useState({});
  const [migrace, setMigrace] = useState(null);

  useEffect(() => {
    if (!open) return;
    salesGet("nastaveni").then((d) => { setData(d); setN(d.nastaveni); }).catch((e) => setChyba(e.message));
  }, [open]);

  const uloz = async () => {
    setBusy("uloz"); setChyba("");
    try { await salesPost("nastaveni", { nastaveni: n }); toast("Nastavení uloženo.", "success"); onClose(); }
    catch (e) { setChyba(e.message); }
    finally { setBusy(""); }
  };
  const test = async (typ) => {
    setBusy(typ);
    try { const r = await salesPost("test", { typ }); setVysledky((v) => ({ ...v, [typ]: { ok: r.ok, text: r.vysledek } })); }
    catch (e) { setVysledky((v) => ({ ...v, [typ]: { ok: false, text: e.message } })); }
    finally { setBusy(""); }
  };
  const spocitej = async () => {
    setBusy("migrace");
    try { setMigrace(await salesPost("migrace", { dry: true })); } catch (e) { setChyba(e.message); }
    finally { setBusy(""); }
  };
  const prevzit = async () => {
    const ok = await confirmDialog({ title: "Převzít leady z Notionu?", description: `${migrace.pocet} rozpracovaných leadů dostane stav a datum dalšího hovoru a objeví se paní v Plánu nebo v Kontaktech. Notion se tím nemění.`, confirmLabel: "Převzít", variant: "primary" });
    if (!ok) return;
    setBusy("migrace");
    try { const r = await salesPost("migrace", { dry: false }); setMigrace(r); toast(`Převzato ${r.pocet} leadů.`, "success"); }
    catch (e) { setChyba(e.message); }
    finally { setBusy(""); }
  };

  const nast = (k, v) => setN((x) => ({ ...x, [k]: v }));
  return (
    <Modal open={open} onClose={onClose} size="md" plnaHlavicka
      header={<HlavickaOkna ikona={Settings} nazev="Nastavení sales" popis={owner ? "Mění jen owner" : "Jen pro čtení"} onClose={onClose} />}
      footer={<>
        <button type="button" className="btn-secondary" onClick={onClose}>Zavřít</button>
        {owner && <button type="button" className="btn-primary" onClick={uloz} disabled={!n || busy === "uloz"} aria-busy={busy === "uloz"}>Uložit</button>}
      </>}>
      {!n ? (chyba ? <Oznameni ton="problem">{chyba}</Oznameni> : <div className="h-40 animate-pulse rounded-xl bg-brand-light" />) : (
        <div className="space-y-5">
          {chyba && <Oznameni ton="problem">{chyba}</Oznameni>}
          <div className="space-y-2">
            <Zapinatko zapnuto={!!n.zapnuto} onZmena={(v) => owner && nast("zapnuto", v)} popisek="Sales zapnuté" ikona={Power} />
            <div className="text-[13px] text-slate-500">Zapnuto: ranní SMS v 9:00, připomínky den před zkušebním a pondělní souhrn do Slacku. Vypnuto: jen ruční akce paní.{n.go_live ? ` Spuštěno ${n.go_live.split("-").reverse().join(". ").replace(/^0/, "")}.` : ""}</div>
          </div>

          <div className="space-y-2">
            <Zapinatko zapnuto={n.klub_pozvanka === true} onZmena={(v) => owner && nast("klub_pozvanka", v)} popisek="Pozvánka do klubu po NQ a prohře" ikona={Mail} />
            <div className="text-[13px] text-slate-500">E-mail s pozvánkou do Klubu FitWay40 přijde 2 dny po uzavření kontaktu jako NQ lead nebo prohra, každé adrese jen jednou. Neposílá se mužům, klientkám, členkám klubu a na blacklist.</div>
          </div>

          <SkupinaNadpis>Přednostní trenéři</SkupinaNadpis>
          <div className="text-[13px] text-slate-500">Zlatě nahoře v nabídce a předvybraní v 1. kroku hovoru.</div>
          <Cipy vice moznosti={(data?.treneri || []).map((t) => ({ key: String(t.id), label: t.jmeno }))} value={(n.prioritni_treneri || []).map(String)} onChange={(v) => owner && nast("prioritni_treneri", v)} />

          <SkupinaNadpis>Zkušební trénink</SkupinaNadpis>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Cena, Kč"><input className="field" inputMode="numeric" value={n.cena_zkusebni} onChange={(e) => nast("cena_zkusebni", e.target.value.replace(/\D/g, ""))} disabled={!owner} /></Field>
            <Field label="Splatnost, dní"><input className="field" inputMode="numeric" value={n.splatnost_dni} onChange={(e) => nast("splatnost_dni", e.target.value.replace(/\D/g, ""))} disabled={!owner} /></Field>
            <Field label="Bonus sales, Kč"><input className="field" inputMode="numeric" value={n.bonus_castka} onChange={(e) => nast("bonus_castka", e.target.value.replace(/\D/g, ""))} disabled={!owner} /></Field>
          </div>
          <div className="text-[13px] text-slate-500">Faktura se vystavuje ve FAPI (účet {n.fapi_ucet}, formulář {n.fapi_formular}) jako zálohová, klientce ji pošle FAPI e‑mailem.</div>

          {owner && (
            <>
              <SkupinaNadpis>Převzetí rozpracovaných leadů z Notionu</SkupinaNadpis>
              {data?.migrace && <div className="text-[13px] text-slate-500">Naposledy převzato {data.migrace.pocet} leadů ({new Date(data.migrace.kdy).toLocaleString("cs-CZ")}).</div>}
              <div className="text-[13px] text-slate-500">Leady v Kvalifikaci podle Kontaktování: Čeká jako noví, Nedovoláno 1 až 3 jako další pokus, Odloženo s datem jako odložené, Odloženo bez data a Zvednuto do Plánu jako Doplnit datum.</div>
              {migrace && (
                <Oznameni ton={migrace.dry ? "info" : "ok"}>
                  {migrace.dry ? "Převzalo by se" : "Převzato"} <b>{migrace.pocet}</b> leadů: {Object.entries(migrace.souhrn || {}).map(([k, v]) => `${k} ${v}`).join(", ") || "nic"}.
                </Oznameni>
              )}
              <div className="flex flex-wrap gap-2">
                <button type="button" className="btn-secondary" onClick={spocitej} disabled={busy === "migrace"} aria-busy={busy === "migrace"}><Download className="h-4 w-4" />Spočítat</button>
                {migrace?.dry && migrace.pocet > 0 && <button type="button" className="btn-primary" onClick={prevzit} disabled={busy === "migrace"}>Převzít {migrace.pocet} leadů</button>}
              </div>

              <SkupinaNadpis>Test po nasazení</SkupinaNadpis>
              <div className="space-y-2">
                {TESTY.map((t) => (
                  <div key={t.typ} className="space-y-1.5">
                    <button type="button" className="btn-secondary w-full justify-start" onClick={() => test(t.typ)} disabled={!!busy} aria-busy={busy === t.typ}><t.ikona className="h-4 w-4" />{t.popis}</button>
                    {vysledky[t.typ] && <Oznameni ton={vysledky[t.typ].ok ? "ok" : "problem"}>{vysledky[t.typ].text}</Oznameni>}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
